import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MAIN_NAVIGATION, NavigationItem } from '../constants/navigation';
import { generateLabel } from '../lib/utility';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useAuthContext } from 'contexts/AuthContext';
import { config } from 'config';

// Match /org-slug/setup at the start of the pathname
// Allow for the optional trailing slash or no trailing slash.
const MATCH_SETUP_TAB = /^\/[^/]*?\/setu(p$|p\/)/;

// Helper function to strip matchMode from navigation items before passing to Ant Design Menu
const stripMatchMode = (item: NavigationItem): NavigationItem => {
  const { matchMode, ...rest } = item;
  if (item.children) {
    return {
      ...rest,
      children: item.children.map(stripMatchMode),
    };
  }
  return rest;
};

export const useNavigation = () => {
  const { t } = useTranslation('globalNavigation');
  const { userPermissions, currentOrg } = useAuthContext();
  const permissions = userPermissions;
  const { checkFlag } = useFeatureFlags();
  const showOnboardingDashboard = checkFlag(
    'hydra(onboarding-dashboard)',
    false
  );
  const location = useLocation();

  // Extract feature flags and computed values
  const features = useMemo(
    () => ({
      showManageTab:
        permissions.canManageAmenities ||
        permissions.canViewBilling ||
        permissions.canViewCheckpoints ||
        permissions.canViewDevices ||
        permissions.canViewOfficeAccess ||
        permissions.canManageGroups ||
        permissions.canManagePlaces ||
        permissions.canManageRoles ||
        permissions.canManageUsers ||
        permissions.canManageWorkplaceServices ||
        permissions.canManageWorkPolicies,

      showScheduleTab: permissions.arePersonalCalendarsEnabled,

      showPeopleTab:
        currentOrg?.hasActiveLocations &&
        permissions.canListUsers &&
        currentOrg?.features?.peopleDirectory,

      showInsightsTab:
        currentOrg?.hasActiveLocations && permissions.canViewAnalytics,

      showVisitsTab: permissions.canViewVisitTab,

      showWorkWeek:
        permissions.canViewWorkweek && currentOrg?.features?.workweek,

      showWorkplaceServicesTab: permissions.canManageWorkplaceServices,

      showDeliveriesTab: permissions.canViewVisitTab,
      showSpaceManagementTab:
        checkFlag('tanuki(disposable-drafts)', false) &&
        (permissions.canManagePlaces || permissions.canManageScenarioPlanning),
    }),
    [permissions, currentOrg, checkFlag]
  );

  // Extract setup-related logic
  const finishSetupLink = useMemo(() => {
    const isMapUploadOnboardingComplete = currentOrg
      ? !currentOrg.mapOnboardingStatus.shouldShowSetup
      : true;

    if (
      isMapUploadOnboardingComplete ||
      !currentOrg?.mapOnboardingStatus.location?.id
    ) {
      return undefined;
    }

    const {
      mapOnboardingStatus: { level, location },
    } = currentOrg;

    const route = showOnboardingDashboard
      ? `/${currentOrg?.slug}/setup/dashboard`
      : level?.id
      ? `/${currentOrg?.slug}/setup/upload-map?locationId=${location?.id}&levelId=${level?.id}`
      : `/${currentOrg?.slug}/setup/add-floor?locationId=${location?.id}`;

    // Check if the setup tab should be shown
    const isFinishSetupFocused = MATCH_SETUP_TAB.test(window.location.pathname);
    const showFinishSetupTab =
      currentOrg?.features?.maps &&
      (isFinishSetupFocused ||
        (!isMapUploadOnboardingComplete && permissions.canManagePlaces));

    return showFinishSetupTab ? route : undefined;
  }, [currentOrg, showOnboardingDashboard, permissions.canManagePlaces]);

  // Extract settings page default logic
  const settingsPageDefault = useMemo(() => {
    if (permissions.canManagePlaces) return 'offices';
    if (permissions.canViewOfficeAccess) return 'access';
    if (permissions.canViewCheckpoints) return 'integrations';
    if (permissions.canViewBilling) return 'billing';
    if (permissions.canViewDevices) return 'devices';
    if (permissions.canManageUsers) return 'users';
    if (permissions.canManageGroups) return 'groups';
    if (permissions.canManageRoles) return 'roles';
    if (permissions.canManageWorkplaceServices) return 'services';
    if (permissions.canManageAmenities) return 'amenities';
    if (permissions.canManageWorkPolicies) return 'work-policies';
    return 'customization';
  }, [permissions]);

  // Extract navigation item processing logic
  const processNavigationItem = useMemo(() => {
    // Visibility configuration - maps item keys to their visibility check functions
    const visibilityChecks: Record<string, () => boolean> = {
      'spaceManagement.title': () => features.showSpaceManagementTab,
      'spaceManagement.scenarioPlanning': () =>
        !!permissions.canManageScenarioPlanning,
      'spaceManagement.layoutTool': () => !!permissions.canManagePlaces,
    };

    return (item: NavigationItem): NavigationItem | null => {
      // Check visibility before processing
      const visibilityCheck = visibilityChecks[item.key];
      if (visibilityCheck && !visibilityCheck()) {
        return null;
      }

      // Handle specific navigation items
      const itemProcessors: Record<string, () => NavigationItem | null> = {
        mySchedule: () => {
          if (!features.showScheduleTab) return null;
          const route = `/schedule/${features.showWorkWeek ? 'week' : 'user'}`;
          return {
            ...item,
            route,
            // Process the label here
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        people: () => {
          if (!features.showPeopleTab) return null;
          const route = '/people';
          return {
            ...item,
            route,
            // Process the label here
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        analytics: () => {
          if (!features.showInsightsTab) return null;
          const route = '/insights';
          return {
            ...item,
            route,
            // Process the label here
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        visits: () => {
          if (!features.showVisitsTab) return null;
          const route = '/visits';
          return {
            ...item,
            route,
            // Process the label here
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        deliveries: () => {
          if (!features.showDeliveriesTab) return null;
          const route = '/deliveries';
          return {
            ...item,
            route,
            // Process the label here
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        tickets: () => {
          if (!features.showWorkplaceServicesTab) return null;
          const route = '/tickets';
          return {
            ...item,
            route,
            // Process the label here
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        manage: () => {
          if (!features.showManageTab || !currentOrg?.slug) return null;
          const route = `/settings/${settingsPageDefault}`;
          const fullRoute = `${config.dashboardUrl}/${currentOrg.slug}${route}`;
          return {
            ...item,
            route: fullRoute,
            label: generateLabel({
              label: t(item.key),
              route: fullRoute,
              isExternal: true,
            }),
          };
        },
      };

      // Process item if it has a specific processor
      if (itemProcessors[item.key]) {
        return itemProcessors[item.key]();
      }

      // Process children recursively
      if (item.children) {
        const processedChildren = item.children
          .map(processNavigationItem)
          .filter((child): child is NavigationItem => child !== null)
          .map((child) => ({
            ...child,
            // Process the child label here
            label: generateLabel({
              label: t(child.key),
              route: child.route ? `/${currentOrg?.slug}${child.route}` : '',
            }),
          }));

        if (processedChildren.length === 0) {
          return null; // Hide parent if no children can be visible
        }

        return {
          ...item,
          // Process the parent label here
          label: t(item.key),
          children: processedChildren,
        };
      }

      // For regular items without children, process the label
      return {
        ...item,
        label: generateLabel({
          label: t(item.key),
          route: item.route ? `/${currentOrg?.slug}${item.route}` : '',
        }),
      };
    };
  }, [
    t,
    currentOrg?.slug,
    permissions.canManagePlaces,
    permissions.canManageScenarioPlanning,
    features.showScheduleTab,
    features.showWorkWeek,
    features.showPeopleTab,
    features.showInsightsTab,
    features.showVisitsTab,
    features.showDeliveriesTab,
    features.showWorkplaceServicesTab,
    features.showSpaceManagementTab,
    features.showManageTab,
    settingsPageDefault,
  ]);

  // Generate main navigation
  const navigation = useMemo(() => {
    return MAIN_NAVIGATION.map(processNavigationItem).filter(
      (item): item is NavigationItem => item !== null
    );
  }, [processNavigationItem]);

  // Combine all navigation (with matchMode intact for route matching)
  const allNavigationItems = useMemo(() => {
    if (!currentOrg || !currentOrg.slug) {
      return [];
    }
    return navigation;
  }, [navigation, currentOrg]);

  // Strip matchMode before passing to Menu component
  const menuItems = useMemo(() => {
    return allNavigationItems.map(stripMatchMode);
  }, [allNavigationItems]);

  // Determine active keys and open keys based on current pathname
  const { activeKeys, openKeys } = useMemo(() => {
    const currentPathname = location.pathname;
    const activeKeys: Set<string> = new Set();
    const openKeys: Set<string> = new Set();

    const findActiveKeys = (items: NavigationItem[], parentKey?: string) => {
      items.forEach((item) => {
        if (item.route && currentOrg?.slug) {
          const fullRoute = `/${currentOrg?.slug}${item.route}`;
          const matchMode = item.matchMode ?? 'inclusive';

          let isMatch = false;
          if (matchMode === 'exact') {
            // Exact match: only match the exact route or route with trailing slash
            isMatch =
              currentPathname === fullRoute ||
              currentPathname === `${fullRoute}/`;
          } else {
            // Inclusive match: check if the route is included in the current pathname
            isMatch = currentPathname.includes(fullRoute);
          }

          if (isMatch) {
            activeKeys.add(item.key);
            if (parentKey) {
              activeKeys.add(parentKey);
              openKeys.add(parentKey);
            }
          }
        }

        if (item.children) {
          findActiveKeys(item.children, item.key);
        }
      });
    };

    findActiveKeys(allNavigationItems);
    return {
      activeKeys: Array.from(activeKeys),
      openKeys: Array.from(openKeys),
    };
  }, [allNavigationItems, currentOrg?.slug, location.pathname]);

  return {
    menuItems,
    activeKeys,
    openKeys,
    finishSetupLink,
  };
};
